import { useRecoilValue } from 'recoil';
import {
  blockCustomizableMetrics,
  blockLimitedRequestSubjects,
  blockMetrics,
  viewSelectedInvestmentGroupInformation,
} from 'venn-state';
import { useMemo } from 'react';
import { assertNotNil, type CustomizableMetric, Numbers } from 'venn-utils';
import type { ColGroupDef } from 'ag-grid-community';
import { get, isNil } from 'lodash';
import type { PGADatapoint, PGARowData } from './types';
import { universeDatapoints } from './types';
import { useMeasureGridText } from '../../grid/AgGridThemeOverrides';
import { useBlockId } from '../../../contexts/BlockIdContext';
import { formatData } from '../../../../data-grid';
import { RIGHT_ALIGN_CLASS } from '../../../customAnalysisContants';
import { BasicHeaderRenderer } from '../../grid/renderers/BasicHeaderRenderer';
import { getItemColor, ItemType } from 'venn-ui-kit';
import { useTheme } from 'styled-components';
import type {
  PeerGroupAnalysisResponse,
  PeerGroupAnalysisSubjectMetricResponse,
  PeerGroupAnalysisUniverseMetricResponse,
} from 'venn-api';

function usePeerGroupsGridColumnDefs() {
  const blockId = useBlockId();
  const theme = useTheme();
  const selectedMetrics = useRecoilValue(blockMetrics(blockId));
  const allMetrics = useRecoilValue(blockCustomizableMetrics(blockId));
  const measureText = useMeasureGridText();
  return useMemo(() => {
    return [
      {
        headerName: '',
        field: 'name',
        minWidth: measureText('Peer Group Information' as const, 'bold'),
      },
      ...selectedMetrics.map((metric, index) => {
        const metricMetadata = assertNotNil(allMetrics.find((customizableMetric) => customizableMetric.key === metric));
        return {
          marryChildren: true,
          headerGroupComponent: BasicHeaderRenderer,
          headerGroupComponentParams: {
            displayName: metricMetadata.label,
            color: getItemColor(theme.Colors, ItemType.Article),
          },
          headerClass: [RIGHT_ALIGN_CLASS],
          cellStyle: {
            textAlign: 'right' as const,
          },
          children: [
            {
              headerName: 'Value',
              colId: `${metric}-value`,
              cellClass: () => ['NUMERIC', RIGHT_ALIGN_CLASS],
              valueGetter: ({ data }) => {
                if (data?.rowId === 'constituents') {
                  const value = data?.rowData[index]?.value;
                  if (isNil(value)) {
                    return '0';
                  }
                  return Numbers.formatNumber(assertNotNil(value), 0);
                }
                if (data?.isMetadata) {
                  return '';
                }
                const value = data?.rowData[index]?.value;
                if (isNil(value)) {
                  return '--';
                }
                return `${formatData(value, metricMetadata.dataType, 2)}`;
              },
            },
            {
              headerName: 'Rank',
              colId: `${metric}-rank`,
              cellClass: () => ['TEXT', RIGHT_ALIGN_CLASS],
              valueGetter: ({ data }) => {
                if (data?.isMetadata || data?.isUniverse) {
                  return '';
                }
                const value = data?.rowData[index]?.rank;
                if (isNil(value)) {
                  return '--';
                }
                return value;
              },
            },
          ],
        } as ColGroupDef<PGARowData>;
      }),
    ];
  }, [theme, measureText, selectedMetrics, allMetrics]);
}

function transformToRowData(
  response: PeerGroupAnalysisResponse,
  subjectIndex: number,
  selectedMetrics: string[],
  allMetrics: CustomizableMetric[],
): PGADatapoint[] {
  return selectedMetrics.map((metric) => {
    const metricMetadata = assertNotNil(allMetrics.find((customizableMetric) => customizableMetric.key === metric));
    const dataForThisMetric = get(response.subjectsData, assertNotNil(metricMetadata.metricPath))[
      subjectIndex
    ] as unknown as PeerGroupAnalysisSubjectMetricResponse;
    return {
      value: dataForThisMetric?.value,
      rank: `${dataForThisMetric?.percentile}`,
    } as PGADatapoint;
  });
}
function transformToUniverseRowData(
  universe: { [key: string]: { [key: string]: PeerGroupAnalysisUniverseMetricResponse } },
  rowId: string,
  selectedMetrics: string[],
  allMetrics: CustomizableMetric[],
): PGADatapoint[] {
  return selectedMetrics.map((metric) => {
    const metricMetadata = assertNotNil(allMetrics.find((customizableMetric) => customizableMetric.key === metric));
    const universeDataForThisMetric = get(
      universe,
      assertNotNil(metricMetadata.metricPath),
    ) as unknown as PeerGroupAnalysisSubjectMetricResponse;
    return {
      value: universeDataForThisMetric?.[rowId],
      rank: undefined,
    } as PGADatapoint;
  });
}

function usePGAGridRowData(response: PeerGroupAnalysisResponse): PGARowData[] {
  const blockId = useBlockId();
  const selectedMetrics = useRecoilValue(blockMetrics(blockId));
  const allMetrics = useRecoilValue(blockCustomizableMetrics(blockId));
  const subjects = useRecoilValue(blockLimitedRequestSubjects(blockId));
  const selectedInvestmentGroupInfoRows = useRecoilValue(viewSelectedInvestmentGroupInformation(blockId));
  return [
    ...subjects.map((subject, subjectIndex) => {
      return {
        name: subject.name,
        rowData: transformToRowData(response, subjectIndex, selectedMetrics, allMetrics),
        rowId: subject.id,
      } as PGARowData;
    }),
    {
      name: 'Peer Group Information',
      rowData: [],
      isMetadata: true,
      rowId: 'peer-group-info',
    } as PGARowData,
    ...universeDatapoints
      .filter((datapoint) => !selectedInvestmentGroupInfoRows || selectedInvestmentGroupInfoRows.includes(datapoint.id))
      .map((datapoint) => {
        return {
          name: datapoint.name,
          id: datapoint.id,
          rowData: transformToUniverseRowData(response.universeData, datapoint.id, selectedMetrics, allMetrics),
          rowId: datapoint.id,
          isMetadata: datapoint.id === 'constituents',
          isUniverse: true,
        };
      }),
  ];
}

export const usePeerGroupsGrid = (data: PeerGroupAnalysisResponse) => {
  const columnDefs = usePeerGroupsGridColumnDefs();
  const rowData = usePGAGridRowData(data);
  return {
    columnDefs,
    rowData,
  };
};
